import { useEffect, useRef, useState } from 'react';

/** The one glyph this component needs that the shared set does not carry. */
const IconChevron = ({ size = 12, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/**
 * The visitor's language selection.
 *
 * One or several. The first is the primary — the language agent replies are
 * translated into and the one a call is placed in — and the rest are shown
 * underneath each reply, so somebody who reads Tamil and Hindi can pick both
 * and check one against the other.
 *
 * The list itself is the server's: languages are rows, so adding one is an
 * INSERT and never a front-end release. This component only decides which of
 * them a visitor has chosen.
 *
 * Built from a button and a checkbox list rather than a `<select multiple>`,
 * which on a phone is a scrolling box that eats the viewport and offers no
 * indication that more than one item can be chosen.
 */
export function LanguagePicker({ languages, selected, primary, max, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);

  // Close on an outside click or Escape. Without this the panel stays over the
  // conversation on a narrow screen with no obvious way back.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!root.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const nameOf = (code) => languages.find((l) => l.code === code)?.nativeName ?? code.toUpperCase();
  const atLimit = selected.length >= max;

  /*
   * The chosen language, named twice: in itself and in English.
   *
   * A visitor scanning for their own language finds it by its own name; the
   * English in brackets is what makes the control legible to whoever is
   * standing next to them, or testing it. Both come from the server's row, so
   * neither is hardcoded here.
   */
  const describe = (code) => {
    const l = languages.find((x) => x.code === code);
    if (!l) return code.toUpperCase();
    return l.nativeName !== l.name ? `${l.nativeName} (${l.name})` : l.nativeName;
  };

  const toggle = (code) => {
    if (selected.includes(code)) {
      // Never leave the visitor with no language at all.
      if (selected.length === 1) return;
      onChange(selected.filter((c) => c !== code));
    } else {
      if (atLimit) return;
      onChange([...selected, code]);
    }
  };

  /** Promote one of the chosen languages to primary by putting it first. */
  const promote = (code) => onChange([code, ...selected.filter((c) => c !== code)]);

  const summary =
    selected.length === 1
      ? describe(primary)
      : `${nameOf(primary)} +${selected.length - 1}`;

  return (
    <div className="langpick" ref={root}>
      <button
        type="button"
        className="langpick-toggle"
        aria-expanded={open}
        aria-haspopup="true"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sr-only">Choose the languages you read</span>
        <span className="langpick-summary">{summary}</span>
        <IconChevron size={14} className={open ? 'langpick-caret open' : 'langpick-caret'} />
      </button>

      {open && (
        <div className="langpick-panel" role="group" aria-label="Languages you read">
          <p className="langpick-help">
            Pick the languages you read. Replies arrive in the first one, with English
            underneath.
          </p>

          {selected.length > 1 && (
            <div className="langpick-chips">
              {selected.map((code) => (
                <button
                  key={code}
                  type="button"
                  className={code === primary ? 'chip primary' : 'chip'}
                  onClick={() => promote(code)}
                  aria-pressed={code === primary}
                  title={
                    code === primary
                      ? `${nameOf(code)} is your main language`
                      : `Make ${nameOf(code)} your main language`
                  }
                >
                  {nameOf(code)}
                  {code === primary && <span className="chip-tag"> · main</span>}
                </button>
              ))}
            </div>
          )}

          <ul className="langpick-list">
            {languages.map((l) => {
              const on = selected.includes(l.code);
              return (
                <li key={l.code}>
                  <label className={on ? 'on' : undefined}>
                    <input
                      type="checkbox"
                      checked={on}
                      // The last remaining language cannot be unticked, and no
                      // more than `max` can be ticked. Disabling says so; a
                      // silently ignored click does not.
                      disabled={(on && selected.length === 1) || (!on && atLimit)}
                      onChange={() => toggle(l.code)}
                    />
                    <span className="langpick-native">{l.nativeName}</span>
                    {l.nativeName !== l.name && <span className="langpick-en">{l.name}</span>}
                  </label>
                </li>
              );
            })}
          </ul>

          {atLimit && <p className="langpick-limit">Up to {max} languages.</p>}
        </div>
      )}
    </div>
  );
}
